import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n/LanguageProvider';
import { ROUTE_NAMES } from '../../Routes/routeNames';
import { VIDEO_LTD_CAMPAIGN, daysUntil, promoStorageKey } from '../../config/promo';

/**
 * Dismissible in-app promo for the limited-time Video Cards lifetime deal.
 *
 * Renders nothing once the campaign deadline passes, so it disappears on its own
 * without needing a deploy to take it down.
 */
const VideoLtdBanner: React.FC = () => {
	const { t } = useT();
	const storageKey = promoStorageKey(VIDEO_LTD_CAMPAIGN.CAMPAIGN_ID);

	// QA override: ?promo=show force-renders the banner, ?promo=reset clears the
	// dismissal for good. Without this, testing the banner means hand-editing
	// localStorage after every dismissal.
	const promoParam = new URLSearchParams(window.location.search).get('promo');

	const [dismissed, setDismissed] = useState<boolean>(() => {
		try {
			if (promoParam === 'reset') localStorage.removeItem(storageKey);
			if (promoParam === 'show' || promoParam === 'reset') return false;
			return localStorage.getItem(storageKey) === '1';
		} catch {
			// Private browsing / blocked storage — show the banner rather than crash.
			return false;
		}
	});

	const daysLeft = daysUntil(VIDEO_LTD_CAMPAIGN.DEADLINE);

	if (dismissed || daysLeft < 0) return null;

	const dismiss = () => {
		setDismissed(true);
		try {
			localStorage.setItem(storageKey, '1');
		} catch {
			// Non-fatal: the banner still closes for this session.
		}
	};

	const countdown = daysLeft === 0 ? t('promo.lastDay') : t('promo.daysLeft', { count: daysLeft });

	return (
		<div className="w-full bg-gradient-to-r from-blue-700 to-indigo-600 text-white">
			<div className="max-w-6xl mx-auto flex items-center gap-3 px-4 py-2.5">
				<span className="hidden sm:inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/15 flex-shrink-0">
					<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
						<path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
					</svg>
				</span>

				<p className="text-sm leading-snug flex-1 min-w-0">
					<span className="font-semibold">{t('promo.headline')}</span>{' '}
					<span className="text-white/85 hidden md:inline">{t('promo.subline')}</span>
				</p>

				<span className="text-[11px] font-semibold uppercase tracking-wide bg-white/15 rounded-full px-2.5 py-1 whitespace-nowrap flex-shrink-0">
					{countdown}
				</span>

				<Link
					to={ROUTE_NAMES.videoCardsUpgrade}
					className="bg-white text-blue-700 text-sm font-semibold rounded-lg px-3.5 py-1.5 hover:bg-blue-50 transition-colors whitespace-nowrap flex-shrink-0"
				>
					{t('promo.cta')}
				</Link>

				<button
					type="button"
					onClick={dismiss}
					aria-label={t('common.close')}
					className="p-1 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
				>
					<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
						<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
					</svg>
				</button>
			</div>
		</div>
	);
};

export default VideoLtdBanner;
